/**
 * Pure TEA update for About Zephyrwrite (#350).
 *
 * Two pieces live here:
 * - `AboutPanel`: the About content (version, copy, Check for updates). Shell:
 *   `AboutPanel.vue`, used by the native About window and the pop-up.
 * - `AboutLauncher`: whether "About" opens the native window (Tauri desktop,
 *   any width) or the pop-up (web and mobile). Composed into the splash and
 *   editor shell models so they keep one `useTea` loop each.
 *
 * Check for updates (#351): idle, checking, up to date, available (with
 * Later), installing, and error with the raw message kept for a disclosure.
 * The AppImage installs in place and then offers Restart now. The .deb and
 * Android APK offer a Download link. A packaged macOS app checks the darwin
 * manifest. With no known source checkout it shows the rebuild steps and
 * does not run them. When detection already found a checkout, Install and
 * restart uses that path. Source and dev builds keep the beta tag flow.
 * Web hides the control. iOS shows a store note.
 */
import { assertNever } from "../lib/assertNever";
import type {
  CheckFailure,
  UpdateChannel,
  UpdateOffer,
} from "../lib/appUpdate";
import { isNothing, just, matchMaybe, nothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";

// --- About panel ------------------------------------------------------------

/**
 * Where updates come from on this surface:
 * - inPlace: the Linux AppImage. The zw-cloud manifest, then a signed
 *   download that replaces the AppImage, then a restart prompt.
 * - link: the .deb and the Android APK. The zw-cloud manifest, then a
 *   Download button that opens the file in the browser.
 * - mac: a packaged macOS app. The darwin manifest. No download. The
 *   rebuild button runs only when a source checkout is already known.
 * - sourceBuild: a source or dev build. The published beta tag check, then
 *   `update-beta.sh`.
 * - mobileStore: iOS. Updates arrive through the app store.
 * - hidden: web (the server always serves the latest build) and builds with
 *   no update path. No button or status line.
 */
export type AboutUpdateSupport =
  | "detecting"
  | "inPlace"
  | "link"
  | "mac"
  | "sourceBuild"
  | "mobileStore"
  | "hidden";

export type AboutUpdateStatus =
  | { readonly kind: "idle" }
  | { readonly kind: "checking" }
  | { readonly kind: "upToDate" }
  | { readonly kind: "available"; readonly offer: UpdateOffer }
  | { readonly kind: "installing"; readonly offer: UpdateOffer }
  /** sourceBuild and mac: the Terminal rebuild started. */
  | { readonly kind: "installStarted"; readonly offer: UpdateOffer }
  /** inPlace: the new AppImage is in place; restart to use it. */
  | { readonly kind: "readyToRestart"; readonly offer: UpdateOffer }
  /** link: the download opened in the browser. */
  | { readonly kind: "downloadOpened"; readonly offer: UpdateOffer }
  | {
      readonly kind: "error";
      readonly during: "check" | "install";
      readonly reason: CheckFailure;
      readonly message: string;
      /** Set on an install failure so Try again can restart that install. */
      readonly offer: Maybe<UpdateOffer>;
    };

export type AboutCopyStatus = "idle" | "copied" | "failed";

export type AboutPanelModel = {
  readonly support: AboutUpdateSupport;
  /** zw-cloud manifest URL for inPlace, link, and mac. Nothing otherwise. */
  readonly manifestUrl: Maybe<string>;
  /**
   * Checkout from `get_beta_source_root`, when that directory already exists.
   * Nothing means a packaged macOS app must show the rebuild steps instead of
   * launching them.
   */
  readonly sourceRoot: Maybe<string>;
  readonly status: AboutUpdateStatus;
  readonly copy: AboutCopyStatus;
  /** A check was asked for before support was known; run it once detected. */
  readonly checkPending: boolean;
};

export const initialAboutPanelModel: AboutPanelModel = {
  support: "detecting",
  manifestUrl: nothing,
  sourceRoot: nothing,
  status: { kind: "idle" },
  copy: "idle",
  checkPending: false,
};

export type AboutPanelMsg =
  | {
      readonly type: "SupportDetected";
      readonly support: AboutUpdateSupport;
      readonly manifestUrl: Maybe<string>;
      readonly sourceRoot: Maybe<string>;
    }
  | { readonly type: "CheckRequested" }
  | { readonly type: "CheckSucceeded"; readonly offer: Maybe<UpdateOffer> }
  | {
      readonly type: "CheckFailed";
      readonly reason: CheckFailure;
      readonly message: string;
    }
  | { readonly type: "LaterRequested" }
  | { readonly type: "InstallRequested" }
  | { readonly type: "InstallCancelled" }
  | { readonly type: "InstallStarted" }
  | { readonly type: "InstallFinished"; readonly installed: Maybe<string> }
  | { readonly type: "DownloadOpened" }
  | { readonly type: "InstallFailed"; readonly message: string }
  | { readonly type: "RestartRequested" }
  | { readonly type: "CopyRequested"; readonly text: string }
  | { readonly type: "CopyFinished"; readonly copied: boolean };

export type AboutPanelCmd =
  /** Nothing: the beta tag check. Just(url): the zw-cloud manifest. */
  | { readonly kind: "checkForUpdate"; readonly manifestUrl: Maybe<string> }
  | { readonly kind: "startSourceUpdate" }
  | { readonly kind: "installInPlace" }
  | { readonly kind: "openDownload"; readonly url: string }
  | { readonly kind: "restartApp" }
  | { readonly kind: "dismissUpdate"; readonly version: string }
  | { readonly kind: "copyText"; readonly text: string };

/** True on surfaces that run an update check. */
function checks(support: AboutUpdateSupport): boolean {
  switch (support) {
    case "inPlace":
    case "link":
    case "mac":
    case "sourceBuild":
      return true;
    case "detecting":
    case "mobileStore":
    case "hidden":
      return false;
    default:
      return assertNever(support);
  }
}

/** The offer an Install click may act on. Nothing while busy. */
function installOffer(status: AboutUpdateStatus): Maybe<UpdateOffer> {
  switch (status.kind) {
    case "available":
    case "downloadOpened":
      return just(status.offer);
    case "error":
      return status.during === "install" ? status.offer : nothing;
    case "idle":
    case "checking":
    case "upToDate":
    case "installing":
    case "installStarted":
    case "readyToRestart":
      return nothing;
    default:
      return assertNever(status);
  }
}

function busy(status: AboutUpdateStatus): boolean {
  return (
    status.kind === "checking" ||
    status.kind === "installing" ||
    status.kind === "installStarted"
  );
}

function startCheck(
  model: AboutPanelModel,
): readonly [AboutPanelModel, readonly AboutPanelCmd[]] {
  return [
    { ...model, status: { kind: "checking" }, checkPending: false },
    [{ kind: "checkForUpdate", manifestUrl: model.manifestUrl }],
  ];
}

/** The command an Install click runs for this support and offer. */
function installCmd(
  support: AboutUpdateSupport,
  offer: UpdateOffer,
  sourceRoot: Maybe<string>,
): Maybe<AboutPanelCmd> {
  switch (support) {
    case "inPlace":
      return just({ kind: "installInPlace" });
    case "mac":
      return matchMaybe(sourceRoot, {
        just: (): Maybe<AboutPanelCmd> => just({ kind: "startSourceUpdate" }),
        nothing: (): Maybe<AboutPanelCmd> => nothing,
      });
    case "sourceBuild":
      return just({ kind: "startSourceUpdate" });
    case "link":
      return matchMaybe(offer.link, {
        just: (url): Maybe<AboutPanelCmd> =>
          just({ kind: "openDownload", url }),
        nothing: (): Maybe<AboutPanelCmd> => nothing,
      });
    case "detecting":
    case "mobileStore":
    case "hidden":
      return nothing;
    default:
      return assertNever(support);
  }
}

export function updateAboutPanel(
  model: AboutPanelModel,
  msg: AboutPanelMsg,
): readonly [AboutPanelModel, readonly AboutPanelCmd[]] {
  switch (msg.type) {
    case "SupportDetected": {
      const next = {
        ...model,
        support: msg.support,
        manifestUrl: msg.manifestUrl,
        sourceRoot: msg.sourceRoot,
      };
      if (checks(msg.support) && model.checkPending) {
        return startCheck(next);
      }
      return [{ ...next, checkPending: false }, []];
    }
    case "CheckRequested":
      if (model.support === "detecting") {
        return [{ ...model, checkPending: true }, []];
      }
      if (checks(model.support) === false || busy(model.status)) {
        return [model, []];
      }
      return startCheck(model);
    case "CheckSucceeded":
      if (model.status.kind !== "checking") return [model, []];
      return [
        {
          ...model,
          status: matchMaybe(msg.offer, {
            just: (offer): AboutUpdateStatus => ({ kind: "available", offer }),
            nothing: (): AboutUpdateStatus => ({ kind: "upToDate" }),
          }),
        },
        [],
      ];
    case "CheckFailed":
      if (model.status.kind !== "checking") return [model, []];
      return [
        {
          ...model,
          status: {
            kind: "error",
            during: "check",
            reason: msg.reason,
            message: msg.message,
            offer: nothing,
          },
        },
        [],
      ];
    case "LaterRequested": {
      if (model.status.kind !== "available") return [model, []];
      return [
        { ...model, status: { kind: "idle" } },
        [{ kind: "dismissUpdate", version: model.status.offer.version }],
      ];
    }
    case "InstallRequested": {
      const offer = installOffer(model.status);
      if (isNothing(offer)) return [model, []];
      const cmd = installCmd(model.support, offer.value, model.sourceRoot);
      if (isNothing(cmd)) return [model, []];
      return [
        { ...model, status: { kind: "installing", offer: offer.value } },
        [cmd.value],
      ];
    }
    case "InstallCancelled": {
      if (model.status.kind !== "installing") return [model, []];
      return [
        { ...model, status: { kind: "available", offer: model.status.offer } },
        [],
      ];
    }
    case "InstallStarted":
      if (model.status.kind !== "installing") return [model, []];
      return [
        {
          ...model,
          status: { kind: "installStarted", offer: model.status.offer },
        },
        [],
      ];
    case "InstallFinished": {
      if (model.status.kind !== "installing") return [model, []];
      const offer = model.status.offer;
      return [
        {
          ...model,
          status: matchMaybe(msg.installed, {
            just: (version): AboutUpdateStatus => ({
              kind: "readyToRestart",
              offer: { ...offer, version },
            }),
            nothing: (): AboutUpdateStatus => ({ kind: "upToDate" }),
          }),
        },
        [],
      ];
    }
    case "DownloadOpened":
      if (model.status.kind !== "installing") return [model, []];
      return [
        {
          ...model,
          status: { kind: "downloadOpened", offer: model.status.offer },
        },
        [],
      ];
    case "InstallFailed":
      if (model.status.kind !== "installing") return [model, []];
      return [
        {
          ...model,
          status: {
            kind: "error",
            during: "install",
            reason: "failed",
            message: msg.message,
            offer: just(model.status.offer),
          },
        },
        [],
      ];
    case "RestartRequested":
      if (model.status.kind !== "readyToRestart") return [model, []];
      return [model, [{ kind: "restartApp" }]];
    case "CopyRequested":
      return [
        { ...model, copy: "idle" },
        [{ kind: "copyText", text: msg.text }],
      ];
    case "CopyFinished":
      return [{ ...model, copy: msg.copied ? "copied" : "failed" }, []];
    default:
      return assertNever(msg);
  }
}

export const updateAboutPanelFn: UpdateFn<
  AboutPanelModel,
  AboutPanelMsg,
  AboutPanelCmd
> = updateAboutPanel;

/**
 * Whether About shows the update button and status line. Hidden on web and
 * builds with no update path, and while support is still detecting.
 */
export function aboutShowsUpdates(support: AboutUpdateSupport): boolean {
  switch (support) {
    case "detecting":
    case "hidden":
      return false;
    case "inPlace":
    case "link":
    case "mac":
    case "sourceBuild":
    case "mobileStore":
      return true;
    default:
      return assertNever(support);
  }
}

function installingText(support: AboutUpdateSupport, version: string): string {
  switch (support) {
    case "inPlace":
      return `Downloading and installing version ${version}…`;
    case "link":
      return "Opening the download…";
    case "mac":
    case "sourceBuild":
    case "detecting":
    case "mobileStore":
    case "hidden":
      return "Installing the update. Zephyrwrite will restart. Closing this window cancels the update.";
    default:
      return assertNever(support);
  }
}

function installErrorText(support: AboutUpdateSupport): string {
  switch (support) {
    case "inPlace":
      return "Couldn’t install the update. Try again.";
    case "link":
      return "Couldn’t open the download. Try again.";
    case "mac":
    case "sourceBuild":
    case "detecting":
    case "mobileStore":
    case "hidden":
      return "Couldn’t start the update. Try again.";
    default:
      return assertNever(support);
  }
}

function checkErrorText(reason: CheckFailure): string {
  switch (reason) {
    case "rateLimited":
      return "Too many update checks right now. Try again later.";
    case "failed":
      return "Couldn’t check for updates. Check your connection and try again.";
    default:
      return assertNever(reason);
  }
}

/** Steps a packaged macOS app shows when no source checkout is known. */
function macManualSteps(version: string): string {
  const bare = version.trim().replace(/^v/i, "");
  return `Version ${version} is available. From a source checkout, run bash scripts/update-beta.sh v${bare}. That checks out the tag, runs npm install and bundle:beta, then opens the new app.`;
}

/** One status line under the button (aria-live polite). "" hides it. */
export function aboutStatusText(model: AboutPanelModel): string {
  switch (model.support) {
    case "detecting":
    case "hidden":
      return "";
    case "mobileStore":
      return "Updates come through your app store.";
    case "inPlace":
    case "link":
    case "mac":
    case "sourceBuild":
      break;
    default:
      return assertNever(model.support);
  }
  const status = model.status;
  switch (status.kind) {
    case "idle":
      return "";
    case "checking":
      return "Checking for updates…";
    case "upToDate":
      return "You’re on the latest version.";
    case "available":
      if (model.support === "mac" && isNothing(model.sourceRoot)) {
        return macManualSteps(status.offer.version);
      }
      return `Version ${status.offer.version} is available.`;
    case "installing":
      return installingText(model.support, status.offer.version);
    case "installStarted":
      return "Update started. Zephyrwrite will quit and restart after the rebuild.";
    case "readyToRestart":
      return `Version ${status.offer.version} is installed. Restart Zephyrwrite to use it.`;
    case "downloadOpened":
      return `Version ${status.offer.version} opened in your browser. Install it to finish updating.`;
    case "error":
      return status.during === "check"
        ? checkErrorText(status.reason)
        : installErrorText(model.support);
    default:
      return assertNever(status);
  }
}

export type AboutPrimaryAction = {
  readonly label: string;
  readonly enabled: boolean;
  /** Nothing when the button only shows a state (disabled). */
  readonly msg: Maybe<AboutPanelMsg>;
};

const CHECK: AboutPanelMsg = { type: "CheckRequested" };
const INSTALL: AboutPanelMsg = { type: "InstallRequested" };
const LATER: AboutPanelMsg = { type: "LaterRequested" };
const RESTART: AboutPanelMsg = { type: "RestartRequested" };

export type AboutSecondaryAction = {
  readonly label: string;
  readonly msg: AboutPanelMsg;
};

/** Later, only while a newer version is being offered. */
export function aboutSecondaryAction(
  model: AboutPanelModel,
): Maybe<AboutSecondaryAction> {
  if (checks(model.support) === false || model.status.kind !== "available") {
    return nothing;
  }
  return just({ label: "Later", msg: LATER });
}

/** True while the primary button shows a spinner (checking or installing). */
export function aboutPrimaryBusy(model: AboutPanelModel): boolean {
  if (checks(model.support) === false) return false;
  switch (model.status.kind) {
    case "checking":
    case "installing":
    case "installStarted":
      return true;
    case "idle":
    case "upToDate":
    case "available":
    case "readyToRestart":
    case "downloadOpened":
    case "error":
      return false;
    default:
      return assertNever(model.status);
  }
}

/** Raw check or install failure, shown only inside the error disclosure. */
export function aboutErrorDetail(model: AboutPanelModel): Maybe<string> {
  if (model.status.kind !== "error") return nothing;
  const message = model.status.message.trim();
  return message.length === 0 ? nothing : just(message);
}

function installLabel(support: AboutUpdateSupport): string {
  switch (support) {
    case "inPlace":
      return "Install update";
    case "link":
      return "Download";
    case "mac":
    case "sourceBuild":
    case "detecting":
    case "mobileStore":
    case "hidden":
      return "Install and restart";
    default:
      return assertNever(support);
  }
}

/** The single primary button in the About action row. */
export function aboutPrimaryAction(model: AboutPanelModel): AboutPrimaryAction {
  if (checks(model.support) === false) {
    return { label: "Check for updates", enabled: false, msg: nothing };
  }
  const status = model.status;
  switch (status.kind) {
    case "idle":
      return { label: "Check for updates", enabled: true, msg: just(CHECK) };
    case "checking":
      return { label: "Checking…", enabled: false, msg: nothing };
    case "upToDate":
      return { label: "Check again", enabled: true, msg: just(CHECK) };
    case "available":
      if (model.support === "mac" && isNothing(model.sourceRoot)) {
        return { label: "Check again", enabled: true, msg: just(CHECK) };
      }
      return {
        label: installLabel(model.support),
        enabled: true,
        msg: just(INSTALL),
      };
    case "installing":
    case "installStarted":
      return {
        label: model.support === "link" ? "Opening…" : "Installing…",
        enabled: false,
        msg: nothing,
      };
    case "readyToRestart":
      return { label: "Restart now", enabled: true, msg: just(RESTART) };
    case "downloadOpened":
      return { label: "Download again", enabled: true, msg: just(INSTALL) };
    case "error":
      return {
        label: "Try again",
        enabled: true,
        msg: just(status.during === "check" ? CHECK : INSTALL),
      };
    default:
      return assertNever(status);
  }
}

export function aboutCopyLabel(copy: AboutCopyStatus): string {
  switch (copy) {
    case "idle":
      return "Copy version";
    case "copied":
      return "Copied";
    case "failed":
      return "Couldn’t copy";
    default:
      return assertNever(copy);
  }
}

/** Pick the update flow for this runtime from the Rust update channel. */
export function aboutSupportFor(
  tauri: boolean,
  mobileShell: boolean,
  channel: UpdateChannel,
): AboutUpdateSupport {
  if (tauri === false) return "hidden";
  switch (channel.kind) {
    case "inPlace":
      return "inPlace";
    case "link":
      return "link";
    case "mac":
      return "mac";
    case "source":
      return mobileShell ? "mobileStore" : "sourceBuild";
    case "none":
      return mobileShell ? "mobileStore" : "hidden";
    default:
      return assertNever(channel);
  }
}

/** The manifest URL the check uses, for inPlace, link, and mac channels. */
export function manifestUrlFor(channel: UpdateChannel): Maybe<string> {
  switch (channel.kind) {
    case "inPlace":
    case "link":
    case "mac":
      return just(channel.manifestUrl);
    case "source":
    case "none":
      return nothing;
    default:
      return assertNever(channel);
  }
}

// --- About launcher ---------------------------------------------------------

/**
 * window: Tauri desktop opens the native About window, even when narrow.
 * dialog: web and the native mobile shell show the pop-up.
 */
export type AboutHost = "window" | "dialog";

export type AboutLauncherModel = {
  readonly host: AboutHost;
  readonly open: boolean;
  /** Start an update check when the pop-up opens ("Check for updates"). */
  readonly checkOnOpen: boolean;
};

export const initialAboutLauncherModel: AboutLauncherModel = {
  host: "dialog",
  open: false,
  checkOnOpen: false,
};

export type AboutLauncherMsg =
  | { readonly type: "HostDetected"; readonly host: AboutHost }
  | { readonly type: "Open"; readonly check: boolean }
  | { readonly type: "Close" };

export type AboutLauncherCmd = {
  readonly kind: "openAboutWindow";
  readonly check: boolean;
};

export function updateAboutLauncher(
  model: AboutLauncherModel,
  msg: AboutLauncherMsg,
): readonly [AboutLauncherModel, readonly AboutLauncherCmd[]] {
  switch (msg.type) {
    case "HostDetected":
      return [{ ...model, host: msg.host }, []];
    case "Open":
      if (model.host === "window") {
        return [model, [{ kind: "openAboutWindow", check: msg.check }]];
      }
      return [{ ...model, open: true, checkOnOpen: msg.check }, []];
    case "Close":
      return [{ ...model, open: false, checkOnOpen: false }, []];
    default:
      return assertNever(msg);
  }
}

export function aboutHostFor(tauri: boolean, mobileShell: boolean): AboutHost {
  return tauri && mobileShell === false ? "window" : "dialog";
}
